import { Outlet } from 'react-router-dom'

import { AvailableModules, Features } from 'generated/featureDefinitions'
import { ExpensesRootPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import subscriptions from 'modules/billing/store/collections/subscriptions'
import CollectionsBootstrap from 'modules/expenses/lib/CollectionsBootstrap'
import expensesSubscriptions, {
  SubscriptionsProvider,
} from 'modules/expenses/store/collections/subscriptions'
import { AccountsPayableUpsellModal } from 'modules/finance/components/AccountsPayableUpsellModal'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import WithPaywall from 'modules/procurement/components/WithPaywall'

import { ExpensesContextProvider, useExpenses } from './ExpensesContext'

const ExpensesOutlet = () => {
  const { navigation } = useNavigation()
  const { data, featureAvailabilities } = useExpenses()

  const subscription = subscriptions.current
  const bankingAccount = data?.banking?.accountsConnection.nodes[0]
  const onboarding = data?.banking?.onboardingsConnection.nodes[0]
  const providersCounter = data?.banking?.providerConfigurationsConnection.totalCount
  const accountProvidersAvailable = providersCounter ? providersCounter > 0 : false
  const expensableGroupsEnabled =
    data?.expenses?.expensableGroupsSettingsConnection?.nodes[0]?.enabled ?? false

  const tabs = [
    ...(data.expenses._policies.expenseManageExpenses.can
      ? [
          {
            active: true,
            name: i18n.t('expenses_v2.tabs.expenses'),
            index: true,
            href: navigation.expenses.list.fullPath(),
          },
        ]
      : []),
  ]

  const back = {
    href: navigation.expenses.fullPath(),
  }

  return (
    <>
      <AccountsPayableUpsellModal source="ap_expenses_upsellcard"></AccountsPayableUpsellModal>
      <Outlet
        context={{
          loading: false,
          tabs,
          back,
          canSyncWithPayroll: featureAvailabilities.reimburseThroughPayroll,
          canSeeSyncWithPayrollDisabled: hasFeature('dev_packaging_paywalls'),
          canSyncWithBanking: featureAvailabilities.reimburseThroughSepa,
          canRestartApprovalFlow: data.expenses._policies.expenseRestartApprovalFlow.can,
          canManagePayrollSettings: data.payroll._policies.companyManagePayroll.can,
          canSeeCompensationsSettings:
            data.compensations._policies.settingsSeeCompensationsSettings.can,
          compensationsEnabled: featureAvailabilities.compensationsEnabled,
          inExpiredFreeTrial: subscription.inExpiredFreeTrial,
          accountProvidersAvailable,
          section: 'company',
          bankingAccount,
          onboarding,
          expensableGroupsEnabled,
        }}
      />
    </>
  )
}

const Container = () => {
  const { company } = useCurrentSession()
  useAsyncData(async () => {
    policyAuthorizations.authorizePolicies('payroll_supplements.supplements.create')
  }, [])

  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const mainLegalEntityId: number = company.legalEntities?.find((node) => node.main == true)!.id

  const { data, error } = useQuery(ExpensesRootPageDocument, {
    variables: {
      companyId: company.id,
      legalEntityId: mainLegalEntityId,
      featureIds: [
        Features.expenses_aeat,
        Features.expenses_report_on_behalf,
        Features.expenses_physical_cards,
        Features.expenses_per_user_pricing_create_expenses,
        Features.expenses_spending_policies,
        Features.projects_management,
        Features.compensations_management,
      ],
    },
  })

  if (error) throw new Error('Error fetching data')
  if (!data) return <Loading />

  const canSeeExpensesSection =
    data.expenses._policies.expenseManageExpenses.can ||
    data.expenses._policies.cardManageCards.can ||
    data.finance._policies.spendingPoliciesSee.can ||
    data.finance._policies.budgetsSeeBudgets.can

  if (!canSeeExpensesSection) return <NotFound />
  return (
    <WithPaywall features={[Features.expenses_core]} modules={[AvailableModules.expenses]}>
      {() => (
        <ExpensesContextProvider data={data}>
          <ExpensesOutlet />
        </ExpensesContextProvider>
      )}
    </WithPaywall>
  )
}

const ExpensesSubscriptionFetcher = ({ children }: React.PropsWithChildren) => {
  const { loading } = useAsyncData(() => {
    const promises = []
    if (hasFeature('dev_expenses_v2_addon') && !hasFeature('dev_packaging_paywalls')) {
      promises.push(expensesSubscriptions.fetch())
    }

    return Promise.all(promises)
  }, [
    location.pathname,
    hasFeature('dev_expenses_v2_addon'),
    !hasFeature('dev_packaging_paywalls'),
  ])

  return loading ? null : <>{children}</>
}

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <ExpensesSubscriptionFetcher>
        <SubscriptionsProvider collection={expensesSubscriptions}>
          <Container />
        </SubscriptionsProvider>
      </ExpensesSubscriptionFetcher>
    </CollectionsBootstrap>
  )
}

export default RootPage
